import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { AccountDetailForSelector } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  buildManualPaymentCaption,
  getManualPaymentCaptionText,
  ManualPaymentAmountSchema,
} from 'features/payOverTime/screens/payments/helpers/Payments.helpers';
import usePayOverTimeManualPaymentDetails from 'features/payOverTime/screens/payments/hooks/usePayOverTimeManualPaymentDetails';
import PayOverTimeAccountSelection from 'features/payOverTime/screens/payments/PayOverTimeAccountSelection/PayOverTimeAccountSelection';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AmountInput from 'shared/components/vxl/AmountInput/AmountInput';
import Button from 'shared/components/vxl/Button/Button';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isIOS } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';

const ManualPayment = () => {
  const router = useRouter();
  const { autoPayEnabled, loanId: paramsLoanId } = useLocalSearchParams<{
    autoPayEnabled?: string;
    loanId: string;
  }>();
  const { bottom } = useSafeAreaInsets();
  const { t } = useTranslation('payOverTime', { keyPrefix: 'manualRepay' });
  const { height } = useWindowDimensions();
  const isKeyboardVisible = useKeyboardVisibility();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );

  const {
    paymentAccounts,
    loanPaymentAmount,
    setLoanPaymentAmount,
    dueDate,
    loanId,
    amountDueNumber,
    outstandingBalanceNumber,
    errorModel,
    loading,
  } = usePayOverTimeManualPaymentDetails(paramsLoanId);

  useViewDidFocusScreenAnalyticsEvent(
    errorModel
      ? {
          key: 'payOverTimeManualPaymentErrorEncountered',
          customAttributes: { error_name: 'details_load_failed' },
        }
      : null,
  );

  const defaultAccount = paymentAccounts[0] ?? null;
  const chosenAccount = selectedAccountId
    ? paymentAccounts.find((account) => account.accountId === selectedAccountId)
    : null;
  const selectedAccount = chosenAccount ?? defaultAccount;

  const numericAmount = Number(loanPaymentAmount);
  const minimumPayment = 0.01;
  const availableBalance = selectedAccount?.availableBalance.amount ?? 0;
  const captionState = {
    amount: numericAmount,
    amountDue: amountDueNumber,
    outstandingBalance: outstandingBalanceNumber,
    minimumPayment,
    availableBalance: Number(availableBalance),
    hasSelectedAccount: Boolean(selectedAccount),
    dueDate,
  };
  const captionResult = buildManualPaymentCaption(captionState);
  const captionText = getManualPaymentCaptionText(captionState, (key, params) =>
    t(key, params),
  );
  const shouldShowAutopayNotice = autoPayEnabled === 'true' && Boolean(dueDate);

  const handleAmountChange = (inputAmount: string) => {
    const result = ManualPaymentAmountSchema.safeParse(inputAmount);
    if (!result.success) {
      return;
    }
    setLoanPaymentAmount(result.data);
  };

  const isDoneDisabled =
    !selectedAccount || captionResult.isError || numericAmount < minimumPayment;

  const handleSelectAccount = (acct: AccountDetailForSelector) => {
    setSelectedAccountId(acct.accountId);
    bottomSheetRef.current?.dismiss();
  };

  const handleSubmit = () => {
    if (isDoneDisabled) {
      return;
    }

    router.navigate({
      pathname: '/pay-over-time/manual-payment/confirm-payment',
      params: {
        repayAmount: loanPaymentAmount,
        accountName: `${selectedAccount.name} • ${selectedAccount.accountNumberLast4}`,
        paymentSchedule: captionText,
        loanId,
        sourceAccountId: selectedAccount.accountId,
      },
    });
  };

  if (!paramsLoanId) {
    return <Redirect href="/" />;
  }

  return (
    <>
      <View style={[styles.screen, { paddingBottom: bottom }]}>
        <KeyboardAvoidingView
          behavior={isIOS ? 'padding' : 'height'}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
          style={styles.keyboardContainer}
        >
          <Stack.Screen
            options={{
              headerShown: true,
              headerBackVisible: true,
              title: t('title'),
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar {...props} />
              ),
            }}
          />

          <ScrollView keyboardShouldPersistTaps="handled">
            <TransferField
              primaryText={
                selectedAccount
                  ? `${selectedAccount.name} • ${selectedAccount.accountNumberLast4}`
                  : t('selectAnAccount')
              }
              secondaryText={
                selectedAccount
                  ? `${t('available')} ${selectedAccount.availableBalance.formattedWithPlusPrefix}`
                  : undefined
              }
              labelText={t('from')}
              avatarAccessory={
                selectedAccount
                  ? {
                      name: VXLComponentId.ICON_AVATAR,
                      id: 'transfer-money-source-varo-logo',
                      props: {
                        variant: 'MD',
                        imageSource: { uri: selectedAccount.iconURL },
                      },
                    }
                  : {
                      name: VXLComponentId.ICON_AVATAR,
                      id: 'transfer-money-source-bank-icon',
                      props: {
                        iconAccessibilityLabel: 'bank-icon',
                        iconKey: 'BANK',
                        variant: 'MD',
                      },
                    }
              }
              onPress={() => bottomSheetRef.current?.present()}
            />

            <View style={styles.percentageMainWrapper}>
              <AmountInput
                value={loanPaymentAmount}
                captionText={captionText}
                onChangeText={handleAmountChange}
                formatAmount
                captionTextColor={
                  captionResult.isError
                    ? theme.color.status.negative
                    : theme.color.text.secondary
                }
                captionTextIcon={
                  captionResult.isError ? 'STATUS_ERROR' : undefined
                }
              />
            </View>
            <PayOverTimeAccountSelection
              bottomSheetRef={bottomSheetRef}
              paymentAccounts={paymentAccounts}
              onSelect={handleSelectAccount}
            />
          </ScrollView>
        </KeyboardAvoidingView>

        <View style={styles.submitButton}>
          {shouldShowAutopayNotice ? (
            <BodyXSText numberOfLines={0} style={styles.applePayNotice}>
              {t('autopayNotice', { date: dueDate })}
            </BodyXSText>
          ) : null}
          <Button
            label={t('buttons.next')}
            type="PRIMARY"
            isDisabled={isDoneDisabled || loading}
            onPress={handleSubmit}
            width="FULL"
            size="LARGE"
          />
        </View>
      </View>
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          errorModel ? (
            <ErrorScreen
              screenType={errorModel.screenType}
              messageHeadline={errorModel.messageHeadline}
              messageBody={errorModel.messageBody}
              primaryButton={{
                title: t('error.button'),
                onPress: () => router.back(),
              }}
            />
          ) : null
        }
      />
    </>
  );
};

const styles = StyleSheet.create({
  applePayNotice: {
    color: theme.color.text.secondary,
  },
  keyboardContainer: {
    flex: 1,
  },
  percentageMainWrapper: {
    height: 200,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  submitButton: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
});

export default ManualPayment;
